// pages/PrivacyPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Switch,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Cookie as CookieIcon,
  ExpandMore as ExpandMoreIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Analytics as AnalyticsIcon,
  Campaign as MarketingIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Gavel as GavelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import {
  getCookieConsent,
  saveConsent,
  resetConsent,
} from '../components/CookieConsent';

/* ============================================================
   PrivacyPage
   ============================================================ */
const PrivacyPage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRtl = language === 'ar';

  const goldAccent = '#E6B800';
  const goldBright = '#FFD700';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const cardBg = 'rgba(255, 255, 255, 0.025)';
  const border = 'rgba(230, 184, 0, 0.15)';

  const [consent, setConsent] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedSection, setExpandedSection] = useState('intro');

  const LAST_UPDATED = '2025-01-15';
  const CONTACT_EMAIL = 'privacy@qrtable.app';
  const CONTACT_PHONE = '+60 18-324 8864';
  const COMPANY_ADDRESS = isRtl
    ? '112، طريق بوكيت بينتانج، 55100 كوالالمبور، ماليزيا'
    : '112, Bukit Bintang Rd, 55100 Kuala Lumpur, Malaysia';

  // Load current consent
  useEffect(() => {
    const saved = getCookieConsent();
    if (saved) {
      setConsent({
        essential: true,
        analytics: !!saved.analytics,
        marketing: !!saved.marketing,
      });
    }
  }, []);

  // SEO
  const seoTitle = isRtl ? 'سياسة الخصوصية | QR Table' : 'Privacy Policy | QR Table';
  const seoDescription = isRtl
    ? 'تعرّف على كيفية جمع واستخدام وحماية بياناتك على منصة QR Table. تحكم كامل في ملفات تعريف الارتباط والتحليلات.'
    : 'Learn how QR Table collects, uses, and protects your data. Full control over cookies and analytics.';

  // Toggle consent and save
  const handleToggle = (key) => {
    if (key === 'essential') return;
    const next = { ...consent, [key]: !consent[key] };
    setConsent(next);
    saveConsent(next);
    setSnackbar({
      open: true,
      message: isRtl
        ? `تم تحديث تفضيلات ${key === 'analytics' ? 'التحليلات' : 'التسويق'}`
        : `${key === 'analytics' ? 'Analytics' : 'Marketing'} preference updated`,
      severity: 'success',
    });
  };

  // Reset all
  const handleReset = () => {
    resetConsent();
    setConsent({ essential: true, analytics: false, marketing: false });
    setSnackbar({
      open: true,
      message: isRtl ? 'تمت إعادة تعيين تفضيلات ملفات تعريف الارتباط' : 'Cookie preferences reset',
      severity: 'info',
    });
    // Reload so the banner reappears
    setTimeout(() => window.location.reload(), 800);
  };

  // ---------- SECTIONS (accordion content) ----------
  const sections = useMemo(
    () => [
      {
        id: 'intro',
        icon: <ShieldIcon />,
        title: isRtl ? 'مقدمة' : 'Introduction',
        body: isRtl
          ? 'مرحباً بك في QR Table. نحن نحترم خصوصيتك ونلتزم بحماية بياناتك الشخصية. توضح هذه السياسة كيفية جمع واستخدام وحماية ومعالجة بياناتك عند استخدام منصتنا وخدماتنا.'
          : 'Welcome to QR Table. We respect your privacy and are committed to protecting your personal data. This policy explains how we collect, use, protect, and process your information when you use our platform and services.',
      },
      {
        id: 'data-collect',
        icon: <StorefrontIcon />,
        title: isRtl ? 'البيانات التي نجمعها' : 'Data we collect',
        list: isRtl
          ? [
              'بيانات الحساب: الاسم، البريد الإلكتروني، رقم الهاتف، صورة الملف الشخصي.',
              'بيانات المتجر (لأصحاب المتاجر): اسم المتجر، العنوان، السجل التجاري، الشعار، بيانات الموقع.',
              'بيانات الطلبات: العناصر المطلوبة، عناوين التوصيل، تاريخ الطلب، حالة الدفع.',
              'بيانات الاستخدام: الصفحات التي تزورها، مدة الجلسة، نوع الجهاز، المتصفح، عنوان IP.',
              'بيانات الموقع: المدينة والمنطقة التي تختارها، أو إحداثيات GPS عند تفعيلها.',
              'ملفات تعريف الارتباط والتقنيات المشابهة (انظر القسم الخاص بها أدناه).',
            ]
          : [
              'Account data: name, email, phone number, profile picture.',
              'Store data (for store owners): store name, address, business license, logo, location.',
              'Order data: items ordered, delivery addresses, order history, payment status.',
              'Usage data: pages visited, session duration, device type, browser, IP address.',
              'Location data: city and area you select, or GPS coordinates when enabled.',
              'Cookies and similar technologies (see section below).',
            ],
      },
      {
        id: 'how-we-use',
        icon: <StarIcon />,
        title: isRtl ? 'كيف نستخدم بياناتك' : 'How we use your data',
        list: isRtl
          ? [
              'تشغيل الخدمة: معالجة الطلبات، إدارة الحساب، إرسال تأكيدات.',
              'التواصل: الرد على استفساراتك، إرسال إشعارات الطلبات والتحديثات.',
              'التحسين: تحليل استخدام المنصة لتطوير الميزات وإصلاح المشكلات.',
              'الأمان: كشف الاحتيال ومنع إساءة الاستخدام وحماية الحسابات.',
              'الامتثال: الوفاء بالالتزامات القانونية والضريبية.',
              'التسويق (بموافقتك فقط): إرسال عروض ومنتجات جديدة.',
            ]
          : [
              'Service operation: processing orders, account management, sending confirmations.',
              'Communication: responding to inquiries, sending order notifications and updates.',
              'Improvement: analyzing platform usage to build features and fix issues.',
              'Security: fraud detection, abuse prevention, account protection.',
              'Compliance: meeting legal and tax obligations.',
              'Marketing (with your consent only): sending offers and new products.',
            ],
      },
      {
        id: 'sharing',
        icon: <StorefrontIcon />,
        title: isRtl ? 'مشاركة البيانات' : 'Data sharing',
        body: isRtl
          ? 'لا نبيع بياناتك الشخصية. نشاركها فقط في الحالات التالية: مع المطاعم/المتاجر لتنفيذ طلبك، مع مزودي الخدمات (الاستضافة، الدفع، التحليلات) بموجب اتفاقيات حماية، عند الاقتضاء القانوني، أو في حالة اندماج/استحواذ.'
          : 'We do not sell your personal data. We only share it: with restaurants/stores to fulfill your order, with service providers (hosting, payment, analytics) under protection agreements, when legally required, or in case of a merger/acquisition.',
      },
      {
        id: 'security',
        icon: <LockIcon />,
        title: isRtl ? 'أمان البيانات' : 'Data security',
        body: isRtl
          ? 'نستخدم تشفير TLS 1.3 أثناء النقل، تشفير في الراحة لقواعد البيانات الحساسة، تحكم صارم في الوصول، مراجعات أمنية دورية، ومراقبة مستمرة. ومع ذلك، لا يوجد نظام آمن بنسبة 100٪ — نوصي باستخدام كلمة مرور قوية وعدم مشاركتها.'
          : 'We use TLS 1.3 encryption in transit, encryption at rest for sensitive databases, strict access controls, periodic security audits, and continuous monitoring. However, no system is 100% secure — we recommend using a strong password and never sharing it.',
      },
      {
        id: 'your-rights',
        icon: <GavelIcon />,
        title: isRtl ? 'حقوقك' : 'Your rights',
        list: isRtl
          ? [
              'الحق في الوصول إلى بياناتك الشخصية التي نحتفظ بها.',
              'الحق في تصحيح أي بيانات غير دقيقة.',
              'الحق في حذف حسابك وبياناتك (مع مراعاة الالتزامات القانونية).',
              'الحق في تصدير بياناتك بصيغة قابلة للقراءة.',
              'الحق في الاعتراض على المعالجة التسويقية.',
              'الحق في سحب الموافقة على ملفات تعريف الارتباط في أي وقت.',
            ]
          : [
              'Right to access your personal data we hold.',
              'Right to correct any inaccurate data.',
              'Right to delete your account and data (subject to legal obligations).',
              'Right to export your data in a readable format.',
              'Right to object to marketing processing.',
              'Right to withdraw cookie consent at any time.',
            ],
      },
      {
        id: 'retention',
        icon: <RefreshIcon />,
        title: isRtl ? 'مدة الاحتفاظ بالبيانات' : 'Data retention',
        body: isRtl
          ? 'نحتفظ ببيانات الحساب طالما حسابك نشط. بيانات الطلبات تُحفظ لمدة 5 سنوات لأغراض محاسبية وضريبية. سجلات التحليلات تُحفظ لمدة 26 شهراً. عند حذف الحساب، تُحذف البيانات الشخصية خلال 30 يوماً (باستثناء ما يلزم الاحتفاظ به قانونياً).'
          : 'We retain account data while your account is active. Order data is kept for 5 years for accounting and tax purposes. Analytics logs are retained for 26 months. When you delete your account, personal data is deleted within 30 days (except what must be retained by law).',
      },
      {
        id: 'children',
        icon: <ShieldIcon />,
        title: isRtl ? 'خصوصية الأطفال' : "Children's privacy",
        body: isRtl
          ? 'منصتنا غير موجهة للأطفال تحت 13 عاماً. لا نجمع بياناتهم عن قصد. إذا اكتشفنا جمع بيانات طفل دون موافقة ولي الأمر، سنحذفها فوراً.'
          : 'Our platform is not directed at children under 13. We do not knowingly collect their data. If we discover we collected a child\'s data without parental consent, we will delete it immediately.',
      },
      {
        id: 'changes',
        icon: <RefreshIcon />,
        title: isRtl ? 'تحديثات السياسة' : 'Policy updates',
        body: isRtl
          ? 'قد نحدّث هذه السياسة من وقت لآخر. عند إجراء تغييرات جوهرية، سنُعلمك عبر البريد الإلكتروني أو إشعار داخل المنصة قبل 14 يوماً من التطبيق. تاريخ "آخر تحديث" معروض أعلى الصفحة.'
          : 'We may update this policy from time to time. For material changes, we will notify you via email or in-app notification at least 14 days before they take effect. The "last updated" date is shown at the top of the page.',
      },
    ],
    [isRtl]
  );

  return (
    <>
      {/* ==================== SEO ==================== */}
      <Helmet htmlAttributes={{ lang: isRtl ? 'ar' : 'en', dir: isRtl ? 'rtl' : 'ltr' }}>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Box
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          bgcolor: '#0A0A0A',
          minHeight: '100vh',
          pb: { xs: 12, md: 14 },
          background: `
            radial-gradient(circle at 10% 0%, rgba(230,184,0,0.05) 0%, transparent 45%),
            radial-gradient(circle at 90% 100%, rgba(230,184,0,0.03) 0%, transparent 45%),
            #0A0A0A
          `,
        }}
      >
        <Container maxWidth="md" sx={{ pt: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
          {/* ============ HEADER ============ */}
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: softGold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                border: `2px solid ${border}`,
              }}
            >
              <ShieldIcon sx={{ fontSize: 34, color: goldBright }} />
            </Box>

            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.7rem', sm: '2.1rem', md: '2.5rem' },
                background: 'linear-gradient(135deg, #FFFFFF, #FFD700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 1.5,
                letterSpacing: -0.5,
              }}
            >
              {isRtl ? 'سياسة الخصوصية' : 'Privacy Policy'}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ mb: 2, gap: 1 }}
            >
              <Chip
                icon={<RefreshIcon sx={{ fontSize: '1rem !important' }} />}
                label={`${isRtl ? 'آخر تحديث' : 'Last updated'}: ${LAST_UPDATED}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.03)',
                  color: '#B8B8B8',
                  border: `1px solid rgba(255,255,255,0.06)`,
                  fontSize: '0.72rem',
                  '& .MuiChip-icon': { color: goldBright },
                }}
              />
              <Chip
                icon={<GavelIcon sx={{ fontSize: '1rem !important' }} />}
                label={isRtl ? 'متوافق مع GDPR' : 'GDPR compliant'}
                size="small"
                sx={{
                  bgcolor: softGold,
                  color: goldBright,
                  border: `1px solid ${border}`,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  '& .MuiChip-icon': { color: goldBright },
                }}
              />
            </Stack>

            <Typography
              sx={{
                color: '#A8A8A8',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                maxWidth: 620,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              {isRtl
                ? 'شفافيتك تهمّنا. تعرّف على كيفية جمعنا واستخدامنا وحمايتنا لبياناتك — وتحكم فيها بالكامل في أي وقت.'
                : 'Your transparency matters to us. Learn how we collect, use, and protect your data — and stay in full control at any time.'}
            </Typography>
          </Box>

          {/* ============ COOKIE CONTROLS (LIVE) ============ */}
          <Card
            sx={{
              bgcolor: cardBg,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              border: `1px solid ${border}`,
              borderRadius: '20px',
              mb: 4,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: `linear-gradient(90deg, transparent, ${goldBright}88, transparent)`,
              },
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    bgcolor: softGold,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CookieIcon sx={{ color: goldBright, fontSize: 22 }} />
                </Box>
                <Box>
                  <Typography
                    component="h2"
                    sx={{ fontWeight: 800, color: '#fff', fontSize: '1.05rem' }}
                  >
                    {isRtl ? 'تحكم في ملفات تعريف الارتباط' : 'Manage your cookies'}
                  </Typography>
                  <Typography sx={{ color: '#8A8A8A', fontSize: '0.75rem', mt: 0.25 }}>
                    {isRtl
                      ? 'يمكنك تغيير تفضيلاتك في أي وقت — تُطبّق فوراً'
                      : 'Change your preferences anytime — takes effect immediately'}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1}>
                {/* Essential */}
                <ConsentToggle
                  icon={<LockIcon sx={{ fontSize: 18 }} />}
                  title={isRtl ? 'ملفات ضرورية' : 'Essential cookies'}
                  description={
                    isRtl
                      ? 'مطلوبة لتسجيل الدخول والسلة والأمان. لا يمكن تعطيلها.'
                      : 'Required for login, cart, and security. Cannot be disabled.'
                  }
                  checked
                  disabled
                  badge={isRtl ? 'دائماً مفعّل' : 'Always on'}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />

                {/* Analytics */}
                <ConsentToggle
                  icon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
                  title={isRtl ? 'تحليلات' : 'Analytics'}
                  description={
                    isRtl
                      ? 'تساعدنا على فهم كيفية استخدام الموقع لتحسين تجربتك.'
                      : 'Help us understand how the site is used so we can improve your experience.'
                  }
                  checked={consent.analytics}
                  onChange={() => handleToggle('analytics')}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />

                {/* Marketing */}
                <ConsentToggle
                  icon={<MarketingIcon sx={{ fontSize: 18 }} />}
                  title={isRtl ? 'تسويق' : 'Marketing'}
                  description={
                    isRtl
                      ? 'تُستخدم لعرض إعلانات أكثر ملاءمة لك على منصات أخرى.'
                      : 'Used to show more relevant ads to you on other platforms.'
                  }
                  checked={consent.marketing}
                  onChange={() => handleToggle('marketing')}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />
              </Stack>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 2 }} />

              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Button
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                  size="small"
                  sx={{
                    color: '#B8B8B8',
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    '&:hover': { color: goldBright, bgcolor: 'transparent' },
                  }}
                >
                  {isRtl ? 'إعادة تعيين' : 'Reset all'}
                </Button>
                <Typography sx={{ color: '#6A6A6A', fontSize: '0.7rem' }}>
                  {isRtl ? 'تُحفظ تلقائياً' : 'Saved automatically'}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* ============ SECTIONS (accordions) ============ */}
          <Box sx={{ mb: 4 }}>
            {sections.map((section) => (
              <Accordion
                key={section.id}
                expanded={expandedSection === section.id}
                onChange={() =>
                  setExpandedSection(expandedSection === section.id ? '' : section.id)
                }
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: cardBg,
                  border: `1px solid rgba(255,255,255,0.06)`,
                  borderRadius: '16px !important',
                  mb: 1.5,
                  overflow: 'hidden',
                  '&::before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderColor: border,
                    boxShadow: '0 8px 24px rgba(230,184,0,0.08)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: goldBright }} />}
                  sx={{
                    px: 2.5,
                    '&.Mui-expanded': { minHeight: 56 },
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '10px',
                        bgcolor: softGold,
                        color: goldBright,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': { fontSize: 18 },
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: '#fff',
                        fontSize: { xs: '0.88rem', sm: '0.95rem' },
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  {section.body && (
                    <Typography
                      sx={{
                        color: '#B8B8B8',
                        fontSize: '0.85rem',
                        lineHeight: 1.8,
                        textAlign: isRtl ? 'right' : 'left',
                      }}
                    >
                      {section.body}
                    </Typography>
                  )}
                  {section.list && (
                    <List dense disablePadding>
                      {section.list.map((item, i) => (
                        <ListItem
                          key={i}
                          disableGutters
                          sx={{
                            alignItems: 'flex-start',
                            py: 0.75,
                            flexDirection: isRtl ? 'row-reverse' : 'row',
                          }}
                        >
                          <ListItemIcon
                            sx={{ minWidth: 28, mt: 0.25, justifyContent: 'center' }}
                          >
                            <CheckCircleIcon
                              sx={{ fontSize: 16, color: goldBright }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{
                              sx: {
                                color: '#B8B8B8',
                                fontSize: '0.85rem',
                                lineHeight: 1.7,
                                textAlign: isRtl ? 'right' : 'left',
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* ============ CONTACT ============ */}
          <Card
            sx={{
              bgcolor: cardBg,
              border: `1px solid rgba(255,255,255,0.06)`,
              borderRadius: '20px',
              mb: 4,
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography
                component="h2"
                sx={{ fontWeight: 800, color: '#fff', fontSize: '1.05rem', mb: 2 }}
              >
                {isRtl ? 'اتصل بنا بخصوص الخصوصية' : 'Contact us about privacy'}
              </Typography>
              <Stack spacing={1.5}>
                <ContactRow
                  icon={<EmailIcon />}
                  label={isRtl ? 'البريد الإلكتروني' : 'Email'}
                  value={CONTACT_EMAIL}
                  href={`mailto:${CONTACT_EMAIL}`}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />
                <ContactRow
                  icon={<PhoneIcon />}
                  label={isRtl ? 'الهاتف' : 'Phone'}
                  value={CONTACT_PHONE}
                  href={`tel:${CONTACT_PHONE.replace(/\s|-/g, '')}`}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />
                <ContactRow
                  icon={<LocationIcon />}
                  label={isRtl ? 'العنوان' : 'Address'}
                  value={COMPANY_ADDRESS}
                  isRtl={isRtl}
                  goldBright={goldBright}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* ============ FOOTER NAV ============ */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Button
              onClick={() => navigate(-1)}
              startIcon={isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
              sx={{
                color: '#B8B8B8',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.82rem',
                px: 2.5,
                py: 1,
                borderRadius: '999px',
                border: `1px solid rgba(255,255,255,0.06)`,
                '&:hover': { color: goldBright, bgcolor: 'rgba(230,184,0,0.05)' },
              }}
            >
              {isRtl ? 'رجوع' : 'Back'}
            </Button>
            <Button
              onClick={() => navigate('/about')}
              startIcon={<StorefrontIcon />}
              sx={{
                color: goldBright,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                px: 2.5,
                py: 1,
                borderRadius: '999px',
                bgcolor: softGold,
                border: `1px solid ${border}`,
                '&:hover': { bgcolor: 'rgba(230,184,0,0.18)' },
              }}
            >
              {isRtl ? 'من نحن' : 'About us'}
            </Button>
          </Stack>

          {/* ============ FOOTER ============ */}
          <Typography
            sx={{
              textAlign: 'center',
              color: '#5A5A5A',
              fontSize: '0.72rem',
              mt: 3,
              lineHeight: 1.6,
            }}
          >
            © {new Date().getFullYear()} QR Table.{' '}
            {isRtl ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
            <br />
            <Box
              component="span"
              sx={{ color: goldBright, fontWeight: 600 }}
            >
              {isRtl ? 'آخر تحديث: ' : 'Last updated: '}
            </Box>
            {LAST_UPDATED}
          </Typography>
        </Container>
      </Box>

      {/* ============ SNACKBAR ============ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            bgcolor: 'rgba(20,20,20,0.96)',
            backdropFilter: 'blur(20px)',
            color: '#fff',
            border: `1px solid ${border}`,
            borderRadius: '14px',
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/* ============================================================
   Sub-components
   ============================================================ */
function ConsentToggle({
  icon,
  title,
  description,
  checked,
  onChange,
  disabled,
  badge,
  isRtl,
  goldBright,
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.05)',
        transition: 'all 0.2s ease',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: 'rgba(230,184,0,0.1)',
            color: goldBright,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: isRtl ? 'right' : 'left' }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>
              {title}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(230,184,0,0.15)',
                  color: goldBright,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Stack>
          <Typography
            sx={{
              color: '#7A7A7A',
              fontSize: '0.72rem',
              lineHeight: 1.5,
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        </Box>
        <Switch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          size="small"
          inputProps={{ 'aria-label': title }}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: goldBright },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              bgcolor: '#E6B800',
              opacity: 0.7,
            },
            '& .MuiSwitch-track': { bgcolor: '#3A3A3A' },
            '& .MuiSwitch-switchBase.Mui-disabled': { color: '#666' },
          }}
        />
      </Stack>
    </Box>
  );
}

function ContactRow({ icon, label, value, href, isRtl, goldBright }) {
  const content = (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        p: 1.25,
        borderRadius: '12px',
        flexDirection: isRtl ? 'row-reverse' : 'row',
        textDecoration: 'none',
        transition: 'all 0.2s ease',
        ...(href && {
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(230,184,0,0.08)' },
        }),
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: 'rgba(230,184,0,0.1)',
          color: goldBright,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          '& .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: isRtl ? 'right' : 'left' }}>
        <Typography
          sx={{
            color: '#8A8A8A',
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );

  if (href) {
    return (
      <Box component="a" href={href} sx={{ textDecoration: 'none', color: 'inherit' }}>
        {content}
      </Box>
    );
  }
  return content;
}

export default PrivacyPage;